import { getEvolutionChainRecommendations } from "../api/evolutions/getEvolutionChainRecommendations";
import { getEvolutionSteps } from "../api/evolutions/getEvolutionSteps";
import { getPathwayOptions } from "../api/evolutions/getPathwayOptions";
import { deriveStepUpgrades } from "./builder";
import { evolutionPathPair } from "./evolutionPath";
// ---------------------------------------------------------------------------
// Stale-response guard
// ---------------------------------------------------------------------------
/**
 * Lets only the most recent request write.
 *
 * ```ts
 * const latest = createLatestOnly();
 * const request = latest.begin();
 * const data = await fetchSomething();
 * if (!latest.isCurrent(request)) return; // a newer request went out
 * ```
 *
 * A counter rather than an AbortController: the response of an abandoned
 * request is still useful to the HTTP cache, and the backend has already
 * done the work by the time we could cancel.
 */
export const createLatestOnly = () => {
    let current = 0;
    return {
        /** Marks a new request as the one that counts; returns its ticket. */
        begin: () => ++current,
        isCurrent: (ticket) => ticket === current,
        /** Retires every request in flight without starting a new one. */
        invalidate: () => {
            current += 1;
        },
    };
};
/**
 * Runs `request` and hands its result to `apply` only if no newer request
 * on the same guard began meanwhile. Errors follow the same rule: a stale
 * failure is dropped rather than reported over a newer success.
 */
export const runLatest = async (guard, request, apply) => {
    const ticket = guard.begin();
    try {
        const result = await request();
        if (!guard.isCurrent(ticket))
            return;
        apply.onResult(result);
    }
    catch (error) {
        if (!guard.isCurrent(ticket))
            return;
        apply.onError?.(error);
    }
};
// ---------------------------------------------------------------------------
// Normalising responses
// ---------------------------------------------------------------------------
/**
 * One `nextSteps` entry as a builder step. The path's last id is the
 * evolution this step would apply; an entry without one identifies nothing
 * and cannot be added to a chain, so it reads as `undefined`.
 */
export const stepFromResponse = (next, isGoalkeeper) => {
    const evolutionId = next.evolutionIdPath?.at(-1);
    if (evolutionId === undefined)
        return undefined;
    return {
        kind: "step",
        evolutionId,
        pathwayPath: next.pathwayPath?.at(-1) ?? [],
        // The endpoint sends flat deltas, not an upgrade list.
        upgrades: next.upgrades ?? deriveStepUpgrades(next, isGoalkeeper),
        metaRating: next.metaRating?.value ?? 0,
        metaRatingDelta: next.metaRating?.delta ?? 0,
        playerRoleId: next.playerRoleId ?? 0,
        versionId: next.versionId,
        metalId: next.metalId,
        rating: next.rating ?? 0,
    };
};
/** Every `nextSteps` entry that names an evolution, in the order sent. */
export const stepsFromResponse = (response, isGoalkeeper) => (response?.nextSteps ?? []).flatMap((next) => {
    const step = stepFromResponse(next, isGoalkeeper);
    return step ? [step] : [];
});
/**
 * One `/chains` entry as a recommended chain. Ids may be withheld (see
 * `EvolutionChainRecommendation`), in which case the chain keeps its length
 * and its facts but names no evolution.
 */
export const chainFromResponse = (next, isGoalkeeper, nameOf) => {
    const evolutionIds = next.evolutionIdPath;
    const pair = evolutionIds
        ? evolutionPathPair(evolutionIds, next.pathwayPath)
        : undefined;
    return {
        kind: "chain",
        evolutionIds,
        pathwayPath: pair?.pathwayPath,
        upgrades: next.upgrades ?? deriveStepUpgrades(next, isGoalkeeper),
        metaRating: next.metaRating?.value ?? 0,
        metaRatingDelta: next.metaRating?.delta ?? 0,
        playerRoleId: next.playerRoleId ?? 0,
        versionId: next.versionId,
        metalId: next.metalId,
        rating: next.rating ?? 0,
        cost: next.cost ?? { coins: 0, points: 0 },
        expiresAt: next.expiresAt,
        tags: next.tags ?? [],
        chainLength: next.chainLength ?? evolutionIds?.length ?? 0,
        chainId: next.chainId ?? "",
        chainName: (evolutionIds ?? [])
            .map((id) => nameOf(id) ?? "")
            .filter(Boolean)
            .join(","),
        requiredGames: next.requiredGames ?? 0,
    };
};
export const chainsFromResponse = (response, isGoalkeeper, nameOf) => (response?.nextSteps ?? []).map((next) => chainFromResponse(next, isGoalkeeper, nameOf));
/** A catalogue lookup for `chainsFromResponse`. */
export const evolutionNameLookup = (evolutions, expiredEvolutions = []) => (id) => evolutions.find((evo) => evo.id === id)?.name ??
    expiredEvolutions.find((evo) => evo.id === id)?.name;
/** A withheld chain with the ids `revealChains` returned for it. */
export const withRevealedIds = (chain, revealed, nameOf) => {
    const pair = evolutionPathPair(revealed.evolutionIds, revealed.pathwayPath);
    return {
        ...chain,
        evolutionIds: pair.evolutionPath,
        pathwayPath: pair.pathwayPath,
        chainName: pair.evolutionPath
            .map((id) => nameOf(id) ?? "")
            .filter(Boolean)
            .join(","),
    };
};
/** Whether the backend withheld the chain ids (see `revealChains`). */
export const chainsAwaitReveal = (chains) => chains.length > 0 && chains[0]?.evolutionIds === undefined;
/** `/one-steps`: the steps available from the chain. */
export const requestBuilderSteps = (request) => getEvolutionSteps({
    resourceId: request.resourceId,
    currentChain: request.chain,
    currentPathwayChain: request.pathwayChain,
    assetId: request.assetId,
    evoPathId: request.evoPathId,
    playerRoleIds: request.playerRoleIds,
    includedEvolutions: request.includedEvolutions,
    filters: request.filters ?? {},
});
/** `/chains`: the chains the solver recommends from the chain. */
export const requestBuilderChains = (request) => getEvolutionChainRecommendations({
    resourceId: request.resourceId,
    currentChain: request.chain,
    currentPathwayChain: request.pathwayChain,
    assetId: request.assetId,
    evoPathId: request.evoPathId,
    playerRoleIds: request.playerRoleIds,
    includedEvolutions: request.includedEvolutions,
    mustIncludeEvolutions: request.mustIncludeEvolutions,
    filters: request.filters,
});
/**
 * `/pathway-options`: the pathways one option away from the draft. `chain`
 * must already end with the pathway evolution and `pathwayChain` with the
 * pathway being tried.
 */
export const requestBuilderPathwayOptions = (request) => getPathwayOptions({
    resourceId: request.resourceId,
    chain: request.chain,
    pathwayChain: request.pathwayChain,
    assetId: request.assetId,
    evoPathId: request.evoPathId,
    playerRoleIds: request.playerRoleIds,
});
//# sourceMappingURL=requests.js.map